import { Chalk } from "chalk";
import dayjs from "dayjs";
import naturalSort from "javascript-natural-sort";
import window, { document, location } from "libs/window";
import capitalize from "lodash-es/capitalize";
import differenceWith from "lodash-es/differenceWith";
import flattenDeep from "lodash-es/flattenDeep";
import fromPairs from "lodash-es/fromPairs";
import isEqual from "lodash-es/isEqual";
import last from "lodash-es/last";
import max from "lodash-es/max";
import min from "lodash-es/min";
import once from "lodash-es/once";
import toPairs from "lodash-es/toPairs";
import uniq from "lodash-es/uniq";
import zipObject from "lodash-es/zipObject";
import type { APIDataset, APIUser, MapEntries } from "types/api_types";
import type { BoundingBoxMinMaxType } from "types/bounding_box";
import type { ArbitraryObject, Comparator } from "types/type_utils";
import type { ColorObject, Point3, TypedArray, Vector3, Vector4, Vector6 } from "viewer/constants";
import type { TreeGroup } from "viewer/model/types/tree_types";
import type { BoundingBoxObject, Mapping, NumberLike, SegmentGroup } from "viewer/store";

type UrlParams = Record<string, string>;

// Fix JS modulo bug
// http://javascript.about.com/od/problemsolving/a/modulobug.htm
export function mod(x: number, n: number) {
  return ((x % n) + n) % n;
}

export function keys<T extends string>(o: Record<T, any>): T[] {
  return Object.keys(o) as Array<keyof typeof o>;
}

export function values<T>(o: { [s: string]: T } | ArrayLike<T>): T[] {
  return Object.values(o);
}

export function entries<T>(o: { [s: string]: T } | ArrayLike<T>): [string, T][] {
  return Object.entries(o);
}

export function map3<A, B>(fn: (arg0: A, arg1: 0 | 1 | 2) => B, tuple: [A, A, A]): [B, B, B] {
  const [x, y, z] = tuple;
  return [fn(x, 0), fn(y, 1), fn(z, 2)];
}

export function take2<A>(tuple: [A, A, A] | [A, A, A, A]): [A, A] {
  return [tuple[0], tuple[1]];
}

export function take3<A>(tuple: [A, A, A, A]): [A, A, A] {
  return [tuple[0], tuple[1], tuple[2]];
}

export function map4<A, B>(
  fn: (arg0: A, arg1: 0 | 1 | 2 | 3) => B,
  tuple: [A, A, A, A],
): [B, B, B, B] {
  const [x, y, z, q] = tuple;
  return [fn(x, 0), fn(y, 1), fn(z, 2), fn(q, 3)];
}

export function floor3(tuple: Vector3): Vector3 {
  const [x, y, z] = tuple;
  return [Math.floor(x), Math.floor(y), Math.floor(z)];
}

naturalSort.insensitive = true;

function getRecursiveValues(obj: ArbitraryObject | Array<any> | string): Array<any> {
  return flattenDeep(getRecursiveValuesUnflat(obj));
}

function getRecursiveValuesUnflat(obj: ArbitraryObject | Array<any> | string): Array<any> {
  if (Array.isArray(obj)) {
    return obj.map(getRecursiveValuesUnflat);
  } else if (obj instanceof Object) {
    return Object.keys(obj).map((key) => getRecursiveValuesUnflat(obj[key]));
  } else {
    return [obj];
  }
}

function cheapSort<T extends string | number>(valueA: T, valueB: T): -1 | 0 | 1 {
  if (valueA < valueB) return -1;
  if (valueA > valueB) return 1;
  return 0;
}

export function unique<T>(array: Array<T>): Array<T> {
  return [...new Set(array)];
}

export function union<T>(iterables: Array<Iterable<T>>): Set<T> {
  const set: Set<T> = new Set();

  for (const iterable of iterables) {
    for (const item of iterable) {
      set.add(item);
    }
  }

  return set;
}

export function parseMaybe(str: string | null | undefined): unknown | null {
  try {
    const parsedJSON = JSON.parse(str || "");

    if (parsedJSON != null) {
      return parsedJSON;
    } else {
      return null;
    }
  } catch (_exception) {
    return null;
  }
}

export async function tryToAwaitPromise<T>(promise: Promise<T>): Promise<T | null | undefined> {
  try {
    return await promise;
  } catch (_exception) {
    return null;
  }
}

export function asAbortable<T>(
  promise: Promise<T>,
  signal: AbortSignal,
  abortError: Error,
): Promise<T> {
  return new Promise(async (resolve, reject) => {
    const abort = () => reject(abortError);

    signal.addEventListener("abort", abort);

    try {
      const value = await promise;
      resolve(value);
    } catch (error) {
      reject(error);
    }

    signal.removeEventListener("abort", abort);
  });
}

export function jsonStringify(json: Record<string, any>) {
  return JSON.stringify(json, null, "  ");
}

export function scrollToTop(): void {
  scrollContainerToTop(null);
}

/**
 * Smoothly scrolls the given container to the top (falling back to the
 * window if the ref isn't set yet). For pages whose content scrolls inside a
 * fixed-height, `overflow: auto` container rather than the window.
 */
export function scrollContainerToTop(container: HTMLElement | null | undefined): void {
  (container ?? window).scrollTo({ top: 0, behavior: "smooth" });
}

export function clamp(min: number, value: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function zeroPad(num: number, zeros: number = 0): string {
  let paddedNum = `${num.toString()}`;

  while (paddedNum.length < zeros) {
    paddedNum = `0${paddedNum}`;
  }

  return paddedNum;
}

export function roundTo(value: number, digits: number): number {
  const digitMultiplier = Math.pow(10, digits);
  return Math.round(value * digitMultiplier) / digitMultiplier;
}

function intToHex(int: number, digits: number = 6): string {
  return ("0".repeat(digits) + int.toString(16)).slice(-digits);
}

export function rgbToInt(color: Vector3): number {
  return (color[0] << 16) + (color[1] << 8) + color[2];
}

export function rgbToHex(color: Vector3): string {
  return `#${color.map((int) => intToHex(Math.round(int), 2)).join("")}`;
}

export function hexToRgb(hex: string): Vector3 {
  const bigint = Number.parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}
/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, l, and a are contained in the set [0, 1] and
 * returns r, g, b, and a in the set [0, 1].
 *
 * Taken from:
 * https://stackoverflow.com/a/9493060
 */
function _hslaToRgba(hsla: Vector4): Vector4 {
  const [h, s, l, a] = hsla;
  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = function hue2rgb(p: number, q: number, t: number) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [r, g, b, a];
}

export function colorObjectToRGBArray({ r, g, b }: ColorObject): Vector3 {
  return [r, g, b];
}

export function getRandomColor(): Vector3 {
  // Generate three values between 0 and 1 that multiplied with 255 will be integers.
  const randomColor = [0, 1, 2].map(() => Math.floor(Math.random() * 256) / 255);
  return randomColor as any as Vector3;
}

// Derives a stable, well-saturated color (normalized 0-1 RGB) from a string, so that e.g. two
// layers with different names get distinct but deterministic colors.
export function stringToColor(str: string): Vector3 {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  const hue = (Math.abs(hash) % 360) / 360;
  const [r, g, b] = _hslaToRgba([hue, 0.7, 0.6, 1]);
  return [r, g, b];
}

export function computeBoundingBoxFromArray(bb: Vector6): BoundingBoxMinMaxType {
  const [x, y, z, width, height, depth] = bb;
  return {
    min: [x, y, z],
    max: [x + width, y + height, z + depth],
  };
}

export function computeBoundingBoxFromBoundingBoxObject(
  bb: BoundingBoxObject,
): BoundingBoxMinMaxType {
  return computeBoundingBoxFromArray([...bb.topLeft, bb.width, bb.height, bb.depth]);
}

export function computeBoundingBoxObjectFromBoundingBox(
  bb: BoundingBoxMinMaxType,
): BoundingBoxObject {
  const boundingBoxArray = computeArrayFromBoundingBox(bb);
  return {
    topLeft: [boundingBoxArray[0], boundingBoxArray[1], boundingBoxArray[2]],
    width: boundingBoxArray[3],
    height: boundingBoxArray[4],
    depth: boundingBoxArray[5],
  };
}

export function computeArrayFromBoundingBox(bb: BoundingBoxMinMaxType): Vector6 {
  return [
    bb.min[0],
    bb.min[1],
    bb.min[2],
    bb.max[0] - bb.min[0],
    bb.max[1] - bb.min[1],
    bb.max[2] - bb.min[2],
  ];
}

export function computeShapeFromBoundingBox(bb: BoundingBoxMinMaxType): Vector3 {
  return [bb.max[0] - bb.min[0], bb.max[1] - bb.min[1], bb.max[2] - bb.min[2]];
}

export function computeVolumeFromBoundingBox(bb: BoundingBoxMinMaxType): number {
  const shape = computeShapeFromBoundingBox(bb);
  return shape[0] * shape[1] * shape[2];
}

export function aggregateBoundingBox(
  boundingBoxes: Array<BoundingBoxObject>,
): BoundingBoxMinMaxType {
  if (boundingBoxes.length === 0) {
    return {
      min: [0, 0, 0],
      max: [0, 0, 0],
    };
  }

  const allCoordinates = [0, 1, 2].map((index) =>
    boundingBoxes
      .map((box) => box.topLeft[index])
      .concat(
        boundingBoxes.map((box) => {
          const bottomRight = [
            box.topLeft[0] + box.width,
            box.topLeft[1] + box.height,
            box.topLeft[2] + box.depth,
          ];
          return bottomRight[index];
        }),
      ),
  );
  const min = [0, 1, 2].map((index) => Math.min(...allCoordinates[index])) as any as Vector3;
  const max = [0, 1, 2].map((index) => Math.max(...allCoordinates[index])) as any as Vector3;
  return {
    min,
    max,
  };
}

export function areBoundingBoxesOverlappingOrTouching(
  firstBB: BoundingBoxMinMaxType,
  secondBB: BoundingBoxMinMaxType,
) {
  let areOverlapping = true;

  for (let dim = 0; dim < 3 && areOverlapping; ++dim) {
    areOverlapping = firstBB.max[dim] >= secondBB.min[dim] && secondBB.max[dim] >= firstBB.min[dim];
  }

  return areOverlapping;
}

export function compareBy<T>(
  selector: (arg0: T) => number,
  isSortedAscending: boolean = true,
): Comparator<T> {
  return (a: T, b: T) => {
    if (!isSortedAscending) {
      [a, b] = [b, a];
    }

    const valueA = selector(a);
    const valueB = selector(b);

    if (typeof valueA !== "number" || typeof valueB !== "number") {
      console.error(
        "Wrong compare method called (compareBy should only be called for numbers). Selector:",
        selector,
      );
      return 0;
    }

    return cheapSort(valueA, valueB);
  };
}

export function localeCompareBy<T>(
  selector: (arg0: T) => string,
  isSortedAscending: boolean = true,
  sortNatural: boolean = true,
): Comparator<T> {
  return (a: T, b: T) => {
    if (!isSortedAscending) {
      [a, b] = [b, a];
    }

    const valueA = selector(a);
    const valueB = selector(b);

    if (typeof valueA !== "string" || typeof valueB !== "string") {
      console.error(
        "Wrong compare method called (localeCompareBy should only be called for strings). Selector:",
        selector,
      );
      return 0;
    }

    // localeCompare is really slow, therefore, we use the naturalSort lib and a cheap sorting otherwise
    return sortNatural ? naturalSort(valueA, valueB) : cheapSort(valueA, valueB);
  };
}

export function stringToNumberArray(s: string): Array<number> {
  // remove leading/trailing whitespaces
  s = s.trim();
  // replace remaining whitespaces with commas
  s = s.replace(/,?\s+,?/g, ",");
  const stringArray = s.split(",");
  const result = [];

  for (const e of stringArray) {
    const newEl = Number.parseFloat(e);

    if (!Number.isNaN(newEl)) {
      result.push(newEl);
    }
  }

  return result;
}

export function numberArrayToVector3(array: Array<number>): Vector3 {
  const output: Vector3 = [0, 0, 0];

  for (let i = 0; i < Math.min(3, array.length); i++) {
    output[i] = array[i];
  }

  return output;
}

export function numberArrayToVector6(array: Array<number>): Vector6 {
  const output: Vector6 = [0, 0, 0, 0, 0, 0];

  for (let i = 0; i < Math.min(6, array.length); i++) {
    output[i] = array[i];
  }

  return output;
}

export function point3ToVector3({ x, y, z }: Point3): Vector3 {
  return [x, y, z];
}

function _vector3ToPoint3([x, y, z]: Vector3): Point3 {
  return {
    x,
    y,
    z,
  };
}

export function isUserTeamManager(user: APIUser): boolean {
  return user.teams.findIndex((team) => team.isTeamManager) >= 0;
}

export function isUserAdmin(user: APIUser): boolean {
  return user.isAdmin;
}

export function isUserAdminOrTeamManager(user: APIUser | null | undefined): boolean {
  return user != null && (isUserAdmin(user) || isUserTeamManager(user));
}

function isUserDatasetManager(user: APIUser): boolean {
  return user.isDatasetManager;
}

export function isUserAdminOrDatasetManager(user: APIUser | null | undefined): boolean {
  return user != null && (isUserAdmin(user) || isUserDatasetManager(user));
}

export function isUserAdminOrManager(user: APIUser | null | undefined): boolean {
  return (
    user != null && (isUserAdmin(user) || isUserTeamManager(user) || isUserDatasetManager(user))
  );
}

export function mayUserEditDataset(user: APIUser | null | undefined, dataset: APIDataset): boolean {
  return (
    isUserAdminOrDatasetManager(user) &&
    user != null &&
    user.organization === dataset.owningOrganization
  );
}

export function getUrlParamsObject(): UrlParams {
  return getUrlParamsObjectFromString(location.search);
}

export function getUrlParamsObjectFromString(str: string): UrlParams {
  // Parse the URL parameters as objects and return it or just a single param
  return str
    .substring(1)
    .split("&")
    .reduce((result: UrlParams, value: string): UrlParams => {
      const parts = value.split("=");

      if (parts[0]) {
        const key = decodeURIComponent(parts[0]);

        if (parts[1]) {
          result[key] = decodeURIComponent(parts[1]);
        } else {
          result[key] = "true";
        }
      }

      return result;
    }, {});
}

export function getUrlParamValue(paramName: string): string {
  const params = getUrlParamsObject();
  return params[paramName];
}

export function hasUrlParam(paramName: string): boolean {
  const params = getUrlParamsObject();
  return Object.hasOwn(params, paramName);
}

export function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;

  const end = !inclusive ? right : ascending ? right + 1 : right - 1;

  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }

  return range;
}

export function sleep(timeout: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}

export function isFileExtensionEqualTo(
  fileName: string | null | undefined,
  extensionOrExtensions: string | Array<string>,
) {
  if (fileName == null) {
    return false;
  }
  const passedExtension = (last(fileName.split(".")) || "").toLowerCase();

  if (Array.isArray(extensionOrExtensions)) {
    return extensionOrExtensions.includes(passedExtension);
  }

  return passedExtension === extensionOrExtensions;
}

// Parses dates in format "Thu Jan 1 00:00:00 1970 +0000".
export function parseCTimeDefaultDate(dateString: string) {
  const commitDateWithoutWeekday = dateString.replace(
    /(Mon)|(Tue)|(Wed)|(Thu)|(Fri)|(Sat)|(Sun)\w*/,
    "",
  );
  return dayjs(commitDateWithoutWeekday, "MMM D HH:mm:ss YYYY ZZ");
}

// Only use this function if you really need a busy wait (useful
// for testing performance-related edge cases). Prefer `sleep`
// otherwise.
function _busyWaitDevHelper(time: number) {
  const start = new Date();
  let now: Date;

  while (true) {
    now = new Date();

    // @ts-expect-error ts-migrate(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
    if (now - start >= time) {
      break;
    }
  }
}

export function animationFrame(maxTimeout?: number): Promise<number | undefined> {
  const rafPromise: Promise<ReturnType<typeof window.requestAnimationFrame>> = new Promise(
    (resolve) => {
      window.requestAnimationFrame(resolve);
    },
  );

  if (maxTimeout == null) {
    return rafPromise;
  }

  const timeoutPromise = sleep(maxTimeout) as Promise<undefined>;
  return Promise.race([rafPromise, timeoutPromise]);
}

export function diffArrays<T>(
  stateA: Array<T>,
  stateB: Array<T>,
): {
  both: Array<T>;
  onlyA: Array<T>;
  onlyB: Array<T>;
} {
  const setA = new Set(stateA);
  const both = stateB.filter((x) => setA.has(x));
  const bothSet = new Set(both);
  const onlyA = stateA.filter((x) => !bothSet.has(x));
  const onlyB = stateB.filter((x) => !bothSet.has(x));
  return {
    both,
    onlyA,
    onlyB,
  };
}

/*
 * Diffs two number based arrays. The input is not manipulated.
 * Returns three arrays in the from of { both, onlyA, onlyB }.
 * both contains the numbers present in both arrays;
 * onlyA the numbers present only in array a;
 * onlyB the numbers present only in array b.
 */
export function diffNumberArrays(
  a: number[],
  b: number[],
): { both: number[]; onlyA: number[]; onlyB: number[] } {
  // Create sorted copies to avoid mutating inputs
  const A = [...a].sort((x, y) => x - y);
  const B = [...b].sort((x, y) => x - y);

  const both: number[] = [];
  const onlyA: number[] = [];
  const onlyB: number[] = [];

  let indexA = 0;
  let indexB = 0;

  while (indexA < A.length && indexB < B.length) {
    if (A[indexA] === B[indexB]) {
      both.push(A[indexA]);
      indexA++;
      indexB++;
    } else if (A[indexA] < B[indexB]) {
      onlyA.push(A[indexA]);
      indexA++;
    } else {
      onlyB.push(B[indexB]);
      indexB++;
    }
  }

  // Remaining elements
  while (indexA < A.length) {
    onlyA.push(A[indexA]);
    indexA++;
  }

  while (indexB < B.length) {
    onlyB.push(B[indexB]);
    indexB++;
  }

  return { both, onlyA, onlyB };
}

export function diffMaps<K, V>(
  stateA: Map<K, V>,
  stateB: Map<K, V>,
  maybeEqualityFn?: (v1: V, v2: V) => boolean,
): {
  changed: Iterable<K>; // Contains the keys K for which stateA[K] !== stateB[K]
  onlyA: Iterable<K>; // Contains the keys K that only exist in A
  onlyB: Iterable<K>; // Contains the keys K that only exist in B
} {
  const equalityFn = maybeEqualityFn ?? ((a, b) => a === b);
  const keysOfA = Array.from(stateA.keys());
  const keysOfB = Array.from(stateB.keys());
  const changed = keysOfA.filter(
    (x) => stateB.has(x) && !equalityFn(stateB.get(x)!, stateA.get(x)!),
  );
  const onlyA = keysOfA.filter((x) => !stateB.has(x));
  const onlyB = keysOfB.filter((x) => !stateA.has(x));
  return {
    changed,
    onlyA,
    onlyB,
  };
}

export function withoutValues<T>(arr: Array<T>, elements: Array<T>): Array<T> {
  // This set-based implementation avoids stackoverflow errors from which
  // _.without(arr, ...elements) suffers.
  // When measured against a chunk-based _.without approach, this implementation
  // is 20% faster.
  // _.pullValues was also tested, but this didn't even terminate in a minute
  // (whereas the other test sets could be tackled in under one second).

  const auxSet = new Set(elements);
  return arr.filter((x) => !auxSet.has(x));
}

export function filterNullValues<T>(arr: Array<T | null | undefined>): T[] {
  return arr.filter((el) => el != null);
}

// Filters an array given a search string. Supports searching for several words as AND query.
// Supports nested properties.
// Its pendant was removed int #7783 as it was unused.
export function filterWithSearchQueryAND<
  T extends Readonly<Record<string, unknown>>,
  P extends keyof T,
>(
  collection: Array<T>,
  properties: Array<P | ((arg0: T) => P | Array<any> | string)>,
  searchQuery: string,
): Array<T> {
  if (searchQuery === "") {
    return collection;
  } else {
    const words = searchQuery
      .split(" ")
      .map((element) => element.toLowerCase().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&"));

    const uniques = uniq(words).filter((element) => element !== "");

    const patterns = uniques.map((pattern) => new RegExp(pattern, "igm"));
    return collection.filter((model) =>
      patterns.every((pattern) =>
        properties.some((fieldName) => {
          const value = typeof fieldName === "function" ? fieldName(model) : model[fieldName];

          if (value !== null && (typeof value === "string" || value instanceof Object)) {
            const recursiveValues = getRecursiveValues(value);
            return recursiveValues.some((v) => v?.toString().match(pattern));
          } else {
            return false;
          }
        }),
      ),
    );
  }
}

export function millisecondsToMinutes(ms: number) {
  return ms / 60000;
}

export function minutesToMilliseconds(min: number) {
  return min * 60000;
}

export function millisecondsToHours(ms: number) {
  const oneHourInMilliseconds = 1000 * 60 * 60;
  return ms / oneHourInMilliseconds;
}

export function isNoEditableElementFocused(): boolean {
  // Returns true if no "meaningful" element has focus — i.e. either document.body
  // is active or the active element is not an interactive input element.
  // This allows keyboard shortcuts to fire even when non-input elements (e.g. the
  // tree hierarchy panel) have focus, while still suppressing them when the user
  // is typing in an <input>, <textarea>, <button>, or contentEditable element.
  const activeElement = document.activeElement;
  if (activeElement == null || activeElement === document.body) {
    return true;
  }
  const tag = activeElement.tagName?.toUpperCase();
  return (
    tag !== "INPUT" &&
    tag !== "TEXTAREA" &&
    tag !== "BUTTON" &&
    !(activeElement as HTMLElement).isContentEditable
  );
}

export function isEditableEventTarget(target: EventTarget | null): boolean {
  if (target == null || !(target instanceof Element)) {
    return false; // not an element (could be Window, Document, Text, ...), ignore
  }
  const element = target as HTMLElement;
  const tag = element.tagName?.toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || element.isContentEditable) {
    return true; // ignore Enter inside these fields
  }
  return false;
}

// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
const areEventListenerOptionsSupported = once(() => {
  let passiveSupported = false;

  try {
    const options = {
      get passive() {
        // This function will be called when the browser
        //   attempts to access the passive property.
        passiveSupported = true;
        return true;
      },
    };
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
    window.addEventListener("test", options, options);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
    window.removeEventListener("test", options, options);
  } catch (_err) {
    passiveSupported = false;
  }

  return passiveSupported;
});

// https://stackoverflow.com/questions/25248286/native-js-equivalent-to-jquery-delegation#
export function addEventListenerWithDelegation(
  element: HTMLElement | Document,
  eventName: string,
  delegateSelector: string,
  handlerFunc: (...args: Array<any>) => any,
  options: Record<string, any> = {},
) {
  const wrapperFunc = function (this: HTMLElement | Document, event: Event) {
    for (
      let { target } = event;
      target && target !== this && target instanceof Element;
      target = target.parentNode
    ) {
      if (target.matches(delegateSelector)) {
        handlerFunc.call(target, event);
        break;
      }
    }
  };

  element.addEventListener(
    eventName,
    wrapperFunc,
    areEventListenerOptionsSupported() ? options : false,
  );
  return {
    [eventName]: wrapperFunc,
  };
}

function _median8(dataArray: Array<number>): number {
  // Returns the median of an already *sorted* array of size 8 (e.g., with sortArray8)
  return Math.round((dataArray[3] + dataArray[4]) / 2);
}

function _mode8(arr: Array<number>): number {
  // Returns the mode of an already *sorted* array of size 8 (e.g., with sortArray8)
  let currentConsecCount = 0;
  let currentModeCount = 0;
  let currentMode = -1;
  let lastEl = null;

  for (let i = 0; i < 8; i++) {
    const el = arr[i];

    if (lastEl === el) {
      currentConsecCount++;

      if (currentConsecCount >= currentModeCount) {
        currentModeCount = currentConsecCount;
        currentMode = el;
      }
    } else {
      currentConsecCount = 1;
    }

    lastEl = el;
  }

  return currentMode;
}

// When an interval greater than RAF_INTERVAL_THRESHOLD is used,
// setTimeout is used instead of requestAnimationFrame.
const RAF_INTERVAL_THRESHOLD = 20;
export function waitForCondition(pred: () => boolean, interval: number = 0): Promise<void> {
  const tryToResolve = (resolve: () => void) => {
    if (pred()) {
      resolve();
    } else if (interval > RAF_INTERVAL_THRESHOLD) {
      setTimeout(() => tryToResolve(resolve), interval);
    } else {
      window.requestAnimationFrame(() => tryToResolve(resolve));
    }
  };

  return new Promise(tryToResolve);
}

export function waitForElementWithId(elementId: string): Promise<any> {
  const tryToResolve = (resolve: (value: any) => void) => {
    const el = document.getElementById(elementId);

    if (el) {
      resolve(el);
    } else {
      window.requestAnimationFrame(() => tryToResolve(resolve));
    }
  };

  return new Promise(tryToResolve);
}

function convertDecToBase256(num: number): Vector4 {
  const sign = Math.sign(num);

  const divMod = (n: number) => [Math.floor(n / 256), n % 256];

  let tmp = Math.abs(num);

  let r: number, g: number, b: number, a: number;
  [tmp, r] = divMod(tmp);

  [tmp, g] = divMod(tmp);

  [tmp, b] = divMod(tmp);

  [tmp, a] = divMod(tmp);

  // Little endian
  return map4((el) => sign * el, [r, g, b, a]);
}

export function castForArrayType(uncastNumber: number, data: TypedArray): number | bigint {
  return data instanceof BigUint64Array || data instanceof BigInt64Array
    ? BigInt(uncastNumber)
    : uncastNumber;
}

function _convertNumberTo64Bit(num: number | bigint | null): [Vector4, Vector4] {
  const [bigNumHigh, bigNumLow] = convertNumberTo64BitTuple(num);

  const low = convertDecToBase256(bigNumLow);
  const high = convertDecToBase256(bigNumHigh);

  return [high, low];
}

export function convertNumberTo64BitTuple(num: number | bigint | null): [number, number] {
  if (num == null || Number.isNaN(num)) {
    return [0, 0];
  }

  let sign: number | null;
  if (typeof num === "bigint") {
    sign = num < 0n ? -1 : 1;
  } else {
    sign = Math.sign(num);
  }

  // Cast to BigInt as bit-wise operations only work with 32 bits,
  // even though Number uses 53 bits.
  const bigNum = BigInt(sign) * BigInt(num);

  const bigNumLow = sign * Number((2n ** 32n - 1n) & bigNum);
  const bigNumHigh = sign * Number(bigNum >> 32n);

  return [bigNumHigh, bigNumLow];
}

export async function promiseAllWithErrors<T>(promises: Array<Promise<T>>): Promise<{
  successes: Array<T>;
  errors: Array<Error>;
}> {
  const successOrErrorObjects = await Promise.all(promises.map((p) => p.catch((error) => error)));
  return successOrErrorObjects.reduce(
    ({ successes, errors }, successOrError) => {
      if (successOrError instanceof Error) {
        return {
          successes,
          errors: errors.concat([successOrError]),
        };
      } else {
        return {
          successes: successes.concat([successOrError]),
          errors,
        };
      }
    },
    {
      successes: [],
      errors: [],
    },
  );
}

// This function will chunk an array of elements by time (or some other numeric value).
// Only subsequent elements are potentially put into the same chunk.
// The mapToTimeFn should be a function that maps from an element to a number.
// It'll return an array of chunks.
export function chunkIntoTimeWindows<T>(
  elements: Array<T>,
  mapToTimeFn: (arg0: T) => number,
  chunkByXMinutes: number,
): Array<Array<T>> {
  let chunkIndex = 0;
  let chunkTime = 0;
  return elements.reduce((chunks: Array<Array<T>>, element: T, index: number) => {
    const elementTime = mapToTimeFn(element);
    if (index === 0) chunkTime = elementTime;

    if (Math.abs(chunkTime - elementTime) > chunkByXMinutes * 60 * 1000) {
      chunkIndex++;
      chunkTime = elementTime;
    }

    if (chunks[chunkIndex] == null) chunks.push([]);
    chunks[chunkIndex].push(element);
    return chunks;
  }, []);
}

// chunkDynamically takes an array of input elements and splits these
// into batches. Instead of using a constant batch size, the elements
// of a batch are measured with a measureFn. Then, each batch is filled
// until the provided minThreshold is exceeded.
// Note that the threshold will be exceeded for each batch
// (except for the last batch which may contain less).
export function chunkDynamically<T>(
  elements: T[],
  minThreshold: number,
  measureFn: (el: T) => number,
): Array<T[]> {
  const batches = [];
  let currentBatch = [];
  let currentSize = 0;

  for (let i = 0; i < elements.length; i++) {
    currentBatch.push(elements[i]);
    currentSize += measureFn(elements[i]);
    if (currentSize > minThreshold || i === elements.length - 1) {
      currentSize = 0;
      batches.push(currentBatch);
      currentBatch = [];
    }
  }
  return batches;
}

export function convertBufferToImage(
  buffer: Uint8Array,
  width: number,
  height: number,
  canvasToMerge: HTMLCanvasElement | null | undefined,
  drawImageIntoCanvasCallback: ((ctx: CanvasRenderingContext2D) => void) | null | undefined,
  flipHorizontally: boolean = false,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    width = Math.round(width);
    height = Math.round(height);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx == null) {
      throw new Error("Could not create canvas 2d context.");
    }
    canvas.width = width;
    canvas.height = height;
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(buffer);
    ctx.putImageData(imageData, 0, 0);

    if (flipHorizontally) {
      ctx.transform(1, 0, 0, -1, 0, height);
      ctx.drawImage(canvas, 0, 0);
      ctx.transform(1, 0, 0, -1, 0, height);
    }

    if (canvasToMerge != null) {
      ctx.drawImage(canvasToMerge, 0, 0);
    }

    if (drawImageIntoCanvasCallback) {
      drawImageIntoCanvasCallback(ctx);
    }
    canvas.toBlob((blob: Blob | null) => {
      canvas.remove();
      resolve(blob);
    });
  });
}

export function getIsInIframe() {
  try {
    return window.self !== window.top;
  } catch (_e) {
    return true;
  }
}

function _getWindowBounds(): [number, number] {
  // Function taken from https://stackoverflow.com/questions/3333329/javascript-get-browser-height.
  let width = 0;
  let height = 0;

  if (typeof window.innerWidth === "number") {
    // Non-IE
    width = window.innerWidth;
    height = window.innerHeight;
  } else if (
    document.documentElement &&
    (document.documentElement.clientWidth || document.documentElement.clientHeight)
  ) {
    // IE 6+ in 'standards compliant mode'
    width = document.documentElement.clientWidth;
    height = document.documentElement.clientHeight;
  } else if (document.body && (document.body.clientWidth || document.body.clientHeight)) {
    // IE 4 compatible
    width = document.body.clientWidth;
    height = document.body.clientHeight;
  }

  return [width, height];
}

export function diffObjects<K extends string | number | symbol, V, Dict extends Record<K, V>>(
  a: Dict,
  b: Dict,
): Partial<Dict> {
  /**
   * Returns the difference between two objects as a partial object.
   *
   * Compares two objects `a` and `b` and returns a new object containing the key-value
   * pairs that are present in `b` but not in `a`, using deep equality comparison.
   * Note that keys that are not present in b but in a are *not* returned.
   *
   * @param {Dict} a - The "original" object to compare to as a base.
   * @param {Dict} b - The "newer" object that will be used for the returned values.
   * @returns {Partial<Dict>} - A partial object containing the key-value pairs that differ.
   *
   * @example
   * const a = { x: 1, y: 2, z: 3 };
   * const b = { x: 1, y: 3, q: 4 }; // y is different, z is missing, q was added
   * diffObjects(a, b); // returns { y: 3, q: 4 }
   */
  return fromPairs(differenceWith(toPairs(b), toPairs(a), isEqual)) as Partial<Dict>;
}

function _diffSets<T>(setA: Set<T>, setB: Set<T>) {
  const aWithoutB = new Set<T>();
  const bWithoutA = new Set<T>();
  const intersection = new Set<T>();

  for (const item of setA) {
    if (setB.has(item)) {
      intersection.add(item);
    } else {
      aWithoutB.add(item);
    }
  }

  for (const item of setB) {
    if (!setA.has(item)) {
      bWithoutA.add(item);
    }
  }

  return {
    aWithoutB: aWithoutB,
    bWithoutA: bWithoutA,
    intersection: intersection,
  };
}

export function fastDiffSetAndMap<T>(setA: Set<T>, mapB: Map<T, T>) {
  /*
   * This function was designed for a special use case within the mapping saga,
   * where a Set of (potentially new) segment IDs is passed for setA and a known mapping from
   * id->id is passed for mapB.
   * The function computes:
   * - aWithoutB: segment IDs that are in setA but not in mapB.keys()
   * - bWithoutA: segment IDs that are in mapB.keys() but not in setA
   * - intersection: a Map only contains keys that are in both setA and mapB.keys() (the values are used from mapB).
   */
  const aWithoutB = new Set<T>();
  const bWithoutA = new Set<T>();
  // This function assumes that the returned intersection is relatively large which is common
  // for the use case it was designed for. Under this assumption, mapB is simply copied to
  // initialize the intersection. Afterwards, items that are not within setA are removed from
  // the intersection.
  const intersection = new Map(mapB);

  for (const item of setA) {
    if (!mapB.has(item)) {
      aWithoutB.add(item);
    }
  }

  for (const item of mapB.keys()) {
    if (!setA.has(item)) {
      bWithoutA.add(item);
      intersection.delete(item);
    }
  }

  return {
    aWithoutB: aWithoutB,
    bWithoutA: bWithoutA,
    intersection: intersection,
  };
}

function _areVec3AlmostEqual(a: Vector3, b: Vector3, epsilon: number = 1e-6): boolean {
  return a.every((v, i) => Math.abs(v - b[i]) < epsilon);
}

export function coalesce<T extends {}>(e: T, token: any): T[keyof T] | null {
  return Object.values(e).includes(token as T[keyof T]) ? token : null;
}

export function pluralize(str: string, count: number, optPluralForm: string | null = null): string {
  if (count === 1) {
    return str;
  }
  if (optPluralForm != null) {
    return optPluralForm;
  }
  return `${str}s`;
}

export function conjugate(
  verbStr: string,
  count: number,
  optThirdForm: string | null = null,
): string {
  if (count >= 2) {
    return verbStr;
  }
  if (optThirdForm != null) {
    return optThirdForm;
  }
  return `${verbStr}s`;
}

export function truncateStringToLength(str: string, length: number): string {
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

export function maxValue(array: Array<number>): number {
  const value = max(array);
  if (value == null) {
    throw Error(`Max of empty array: ${array}`);
  }
  return value;
}

export function minValue(array: Array<number>): number {
  const value = min(array);
  if (value == null) {
    throw Error(`Min of empty array: ${array}`);
  }
  return value;
}

/*
 * Iterates over arbitrary objects recursively and calls the callback function.
 */
type Obj = Record<string, unknown>;
export const deepIterate = (obj: Obj | Obj[] | null, callback: (val: unknown) => void) => {
  if (obj == null) {
    return;
  }
  const items = Array.isArray(obj) ? obj : Object.values(obj);
  items.forEach((item) => {
    callback(item);

    if (typeof item === "object") {
      // We know that item is an object or array which matches deepIterate's signature.
      // However, TS doesn't infer this.
      // @ts-expect-error
      deepIterate(item, callback);
    }
  });
};

export function getFileExtension(fileName: string): string {
  const filenameParts = fileName.split(".");
  const fileExtension = filenameParts[filenameParts.length - 1].toLowerCase();
  return fileExtension;
}

export class SoftError extends Error {}

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  // Strongly typed helper to filter any non empty values from an array
  // e.g. [1, 2, undefined].filter(notEmpty) => type should be number[]
  // Source https://github.com/microsoft/TypeScript/issues/45097#issuecomment-882526325
  return value !== null && value !== undefined;
}

export function isNumberMap(x: Map<NumberLike, NumberLike>): x is Map<number, number> {
  const { value } = x.entries().next();
  if (value === undefined) {
    // Let's assume a number map when the map is empty.
    return true;
  }
  return Boolean(typeof value[0] === "number");
}

export function getAdaptToTypeFunction(mapping: Mapping | null | undefined) {
  return mapping && isNumberMap(mapping) ? (el: number) => el : (el: number) => BigInt(el);
}

export function getAdaptToTypeFunctionFromList<T extends number | bigint>(list: Array<T>) {
  return list[0] == null || Boolean(typeof list[0] === "number")
    ? (el: NumberLike) => el
    : (el: NumberLike) => BigInt(el);
}

export function isBigInt(x: NumberLike): x is bigint {
  return typeof x === "bigint";
}

function _assertNever(value: never): never {
  throw new Error(`Unexpected value that is not 'never': ${JSON.stringify(value)}`);
}

/**
 * Returns a URL safe, base 62 encoded hash code from a string
 * @param  {String} str The string to hash.
 * @return {string}    A 32bit integer hash code encoded in base 62.
 * @see https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript (original link is dead)
 */
export function computeHash(str: string): string | undefined {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    let chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  const hashString = encodeToBase62(hash);
  return hashString;
}

export function encodeToBase62(numberToEncode: number): string {
  const base62Chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  if (numberToEncode === 0) return base62Chars[0];
  if (numberToEncode === -1) return base62Chars[61];
  let encoded = "";
  let num = numberToEncode;
  while (num !== 0 && num !== -1) {
    // for positive numberToEncode, num will eventually be 0, for negative numberToEncode, num will eventually be -1
    const modulo = mod(num, 62);
    encoded = base62Chars[modulo] + encoded;
    num = Math.floor(num / 62);
  }
  return encoded;
}

export function safeNumberToStr(num: number): string {
  if (typeof num === "number") {
    return `${num}`;
  }
  return "NaN";
}

export function generateRandomId(length: number) {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export function getPhraseFromCamelCaseString(stringInCamelCase: string): string {
  return stringInCamelCase
    .split(/(?=[A-Z])/)
    .map((word) => capitalize(word.replace(/(^|\s)td/, "$13D")))
    .join(" ");
}

export function mapGroupsDeep<Group extends TreeGroup | SegmentGroup, R>(
  groups: Group[],
  mapFn: (group: Group, mappedChildren: R[]) => R,
): R[] {
  return groups.map((group: Group) => {
    const mappedChildren = mapGroupsDeep(group.children as Group[], mapFn);
    return mapFn(group, mappedChildren);
  });
}

export function safeZipObject<K extends string | number | symbol, V>(
  keys: K[],
  values: V[],
): Record<K, V> {
  if (keys.length !== values.length) {
    throw new Error("Cannot construct objects because keys and values don't match in length.");
  }
  return zipObject(keys, values) as Record<K, V>;
}

export function mapEntriesToMap<K extends string | number | symbol, V>(
  entries: MapEntries<K, V>,
): Record<K, V> {
  const dict = {} as Record<K, V>;
  for (const entry of entries) {
    dict[entry.id] = entry.value;
  }
  return dict;
}

export function areSetsEqual<T>(setA: Set<T>, setB: Set<T>) {
  if (setA.size !== setB.size) return false;
  for (const val of setA) {
    if (!setB.has(val)) return false;
  }
  return true;
}

// Adds a value to the Set stored under `key`, creating the Set if it does not exist yet.
export function addToSetMap<K, V>(map: Map<K, Set<V>>, key: K, value: V) {
  let set = map.get(key);
  if (set == null) {
    set = new Set();
    map.set(key, set);
  }
  set.add(value);
}

// Sets `innerKey -> innerValue` in the inner Map stored under `key`, creating the inner Map if it
// does not exist yet.
export function addToNestedMap<K, IK, IV>(
  map: Map<K, Map<IK, IV>>,
  key: K,
  innerKey: IK,
  innerValue: IV,
) {
  let innerMap = map.get(key);
  if (innerMap == null) {
    innerMap = new Map();
    map.set(key, innerMap);
  }
  innerMap.set(innerKey, innerValue);
}

// ColoredLogger can be used to make certain log outputs easier to find (especially useful
// when automatic logging of redux actions is enabled which makes the overall logging
// very verbose).
const chalk = new Chalk({ level: 3 });
export const ColoredLogger = {
  log: (...args: unknown[]) => {
    // Simple wrapper to allow easy switching from colored to non-colored logs
    console.log(...args);
  },
  logRed: (str: string, ...args: unknown[]) => {
    console.log(chalk.bgRed.black(str), ...args);
  },
  logGreen: (str: string, ...args: unknown[]) => {
    console.log(chalk.bgGreen.black(str), ...args);
  },
  logYellow: (str: string, ...args: unknown[]) => {
    console.log(chalk.bgYellow.black(str), ...args);
  },
  logBlue: (str: string, ...args: unknown[]) => {
    console.log(chalk.bgBlue.black(str), ...args);
  },
};

export async function retryAsyncFunction<T>(
  fn: () => Promise<T>,
  retryCount: number = 3, // 0 retries would only do 1 attempt
  exponentialDelayFactor: number = 1000,
): Promise<T> {
  let currentAttempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (exception) {
      if (currentAttempt === retryCount) {
        throw exception;
      } else {
        console.warn("Failed to run async function due to", exception, ". Will retry now.");
      }
      currentAttempt++;
      await sleep(exponentialDelayFactor * 2 ** currentAttempt);
    }
  }
}

/**
 * Converts a string to a boolean value.
 * Returns false for invalid inputs.
 */
function _stringToBoolean(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return true;
  return false;
}

export function isWindows(): boolean {
  return navigator.platform.includes("Win");
}

export function replaceOrAdd<T>(
  elements: T[],
  newElement: T,
  predicate: (element: T) => boolean,
): T[] {
  /*
   * Insert `newElement` into `elements`. `predicate` will
   * be called for all existing elements. If it returns true
   * for one, that element will be replaced with newElement.
   * If the predicate always returns false, the newElement
   * will be appended.
   *
   * Returns a new array (and doesn't modify the input).
   */
  const index = elements.findIndex(predicate);
  if (index === -1) {
    return elements.concat([newElement]);
  }
  const copy = elements.slice();
  copy.splice(index, 1, newElement);
  return copy;
}
